import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Plus, Trash2, Upload, Image, Settings, Home, User, ShoppingBag, X, GripVertical } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ================== SITE SETTINGS COMPONENT ==================
export const SiteSettingsEditor = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/cms/site-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load site settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/cms/site-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Site settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div className="animate-pulse text-muted-foreground text-center py-8">Loading settings...</div>;
  }

  return (
    <div className="space-y-6" data-testid="site-settings-editor">
      <div className="flex justify-between items-center">
        <h2 className="font-heading text-2xl">Site Settings</h2>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="bg-card rounded-2xl p-6 shadow-sm space-y-6">
        {/* Business Info */}
        <div>
          <h3 className="font-medium text-lg mb-4">Business Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="business_name">Business Name</Label>
              <Input
                id="business_name"
                value={settings?.business_name || ''}
                onChange={(e) => updateField('business_name', e.target.value)}
                data-testid="input-business-name"
              />
            </div>
            <div>
              <Label htmlFor="tagline">Tagline</Label>
              <Input
                id="tagline"
                value={settings?.tagline || ''}
                onChange={(e) => updateField('tagline', e.target.value)}
                data-testid="input-tagline"
              />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div>
          <h3 className="font-medium text-lg mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={settings?.phone || ''}
                onChange={(e) => updateField('phone', e.target.value)}
                data-testid="input-phone"
              />
            </div>
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={settings?.email || ''}
                onChange={(e) => updateField('email', e.target.value)}
                data-testid="input-email"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={settings?.address || ''}
                onChange={(e) => updateField('address', e.target.value)}
                data-testid="input-address"
              />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div>
          <h3 className="font-medium text-lg mb-4">Social Media Links</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="social_facebook">Facebook URL</Label>
              <Input
                id="social_facebook"
                value={settings?.social_facebook || ''}
                onChange={(e) => updateField('social_facebook', e.target.value)}
                placeholder="https://facebook.com/..."
              />
            </div>
            <div>
              <Label htmlFor="social_instagram">Instagram URL</Label>
              <Input
                id="social_instagram"
                value={settings?.social_instagram || ''}
                onChange={(e) => updateField('social_instagram', e.target.value)}
                placeholder="https://instagram.com/..."
              />
            </div>
            <div>
              <Label htmlFor="social_linkedin">LinkedIn URL</Label>
              <Input
                id="social_linkedin"
                value={settings?.social_linkedin || ''}
                onChange={(e) => updateField('social_linkedin', e.target.value)}
                placeholder="https://linkedin.com/in/..."
              />
            </div>
            <div>
              <Label htmlFor="social_twitter">Twitter/X URL</Label>
              <Input
                id="social_twitter"
                value={settings?.social_twitter || ''}
                onChange={(e) => updateField('social_twitter', e.target.value)}
                placeholder="https://twitter.com/..."
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div>
          <h3 className="font-medium text-lg mb-4">Footer</h3>
          <div>
            <Label htmlFor="footer_text">Footer Text</Label>
            <Input
              id="footer_text"
              value={settings?.footer_text || ''}
              onChange={(e) => updateField('footer_text', e.target.value)}
            />
          </div>
        </div>

        {/* Display Settings */}
        <div>
          <h3 className="font-medium text-lg mb-4">Display Settings</h3>
          <div className="flex items-center justify-between p-4 border border-border rounded-xl mb-4">
            <div>
              <Label htmlFor="show_prices" className="text-base font-medium">Show Prices on Service Pages</Label>
              <p className="text-sm text-muted-foreground mt-1">
                When disabled, prices will only be visible during the booking process
              </p>
            </div>
            <Switch
              id="show_prices"
              checked={settings?.show_prices_on_services !== false}
              onCheckedChange={(checked) => updateField('show_prices_on_services', checked)}
              data-testid="toggle-show-prices"
            />
          </div>
        </div>

        {/* Logo Font Style */}
        <div>
          <h3 className="font-medium text-lg mb-4">Logo Font Style</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Choose an elegant font style for the "KBC" logo in the navigation
          </p>
          
          {/* Font Preview */}
          <div className="bg-primary/5 rounded-xl p-6 mb-4 text-center">
            <p className="text-sm text-muted-foreground mb-2">Preview</p>
            <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600&family=Cormorant+Garamond:wght@400;500;600&family=Libre+Baskerville:wght@400;700&family=Cinzel:wght@400;500;600&family=Bodoni+Moda:wght@400;500;600&display=swap" rel="stylesheet" />
            <span 
              className="text-4xl tracking-wider"
              style={{ 
                fontFamily: {
                  'playfair': '"Playfair Display", serif',
                  'cormorant': '"Cormorant Garamond", serif',
                  'baskerville': '"Libre Baskerville", serif',
                  'cinzel': '"Cinzel", serif',
                  'bodoni': '"Bodoni Moda", serif'
                }[settings?.logo_font_style || 'playfair']
              }}
            >
              KBC
            </span>
          </div>

          {/* Font Options Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { id: 'playfair', name: 'Playfair Display', description: 'Classic & sophisticated', font: '"Playfair Display", serif' },
              { id: 'cormorant', name: 'Cormorant Garamond', description: 'Light & refined', font: '"Cormorant Garamond", serif' },
              { id: 'baskerville', name: 'Libre Baskerville', description: 'Traditional elegance', font: '"Libre Baskerville", serif' },
              { id: 'cinzel', name: 'Cinzel', description: 'Refined uppercase', font: '"Cinzel", serif' },
              { id: 'bodoni', name: 'Bodoni Moda', description: 'Fashion-forward', font: '"Bodoni Moda", serif' }
            ].map((fontOption) => (
              <button
                key={fontOption.id}
                type="button"
                onClick={() => updateField('logo_font_style', fontOption.id)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  settings?.logo_font_style === fontOption.id || (!settings?.logo_font_style && fontOption.id === 'playfair')
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid={`font-option-${fontOption.id}`}
              >
                <span 
                  className="text-2xl tracking-wider block mb-2"
                  style={{ fontFamily: fontOption.font }}
                >
                  KBC
                </span>
                <span className="text-sm font-medium block">{fontOption.name}</span>
                <span className="text-xs text-muted-foreground">{fontOption.description}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Change Password */}
        <div>
          <h3 className="font-medium text-lg mb-4">Change Password</h3>
          <PasswordChangeForm />
        </div>
      </div>
    </div>
  );
};

// Password Change Form Component
const PasswordChangeForm = () => {
  const { token } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleChangePassword} className="space-y-4 border border-border rounded-xl p-4">
      <div>
        <Label htmlFor="current_password">Current Password</Label>
        <Input
          id="current_password"
          type={showPasswords ? 'text' : 'password'}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          data-testid="input-current-password"
        />
      </div>
      <div>
        <Label htmlFor="new_password">New Password</Label>
        <Input
          id="new_password"
          type={showPasswords ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength={6}
          data-testid="input-new-password"
        />
      </div>
      <div>
        <Label htmlFor="confirm_password">Confirm New Password</Label>
        <Input
          id="confirm_password"
          type={showPasswords ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          minLength={6}
          data-testid="input-confirm-password"
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="show_passwords"
          checked={showPasswords}
          onChange={(e) => setShowPasswords(e.target.checked)}
          className="rounded"
        />
        <Label htmlFor="show_passwords" className="text-sm font-normal cursor-pointer">Show passwords</Label>
      </div>
      <Button type="submit" disabled={saving} data-testid="change-password-btn">
        {saving ? 'Changing...' : 'Change Password'}
      </Button>
    </form>
  );
};

// ================== HOMEPAGE EDITOR COMPONENT ==================
export const HomepageEditor = () => {
  const { token } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/admin/cms/homepage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load homepage content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/cms/homepage`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Homepage content saved successfully');
    } catch (error) {
      toast.error('Failed to save homepage content');
    } finally {
      setSaving(false);
    }
  };

  const updateHero = (field, value) => {
    setContent(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: value }
    }));
  };

  const updateFeature = (index, field, value) => {
    const newFeatures = [...(content.features || [])];
    newFeatures[index] = { ...newFeatures[index], [field]: value };
    setContent(prev => ({ ...prev, features: newFeatures }));
  };

  const addFeature = () => {
    const newFeatures = [...(content.features || []), {
      id: Date.now().toString(),
      icon: 'Star',
      title: 'New Feature',
      description: 'Feature description'
    }];
    setContent(prev => ({ ...prev, features: newFeatures }));
  };

  const removeFeature = (index) => {
    const newFeatures = (content.features || []).filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, features: newFeatures }));
  };

  const updateTestimonial = (index, field, value) => {
    const newTestimonials = [...(content.testimonials || [])];
    newTestimonials[index] = { ...newTestimonials[index], [field]: value };
    setContent(prev => ({ ...prev, testimonials: newTestimonials }));
  };

  const addTestimonial = () => {
    const newTestimonials = [...(content.testimonials || []), {
      id: Date.now().toString(),
      quote: 'New testimonial quote',
      author_name: 'Client Name',
      author_title: 'Client',
      rating: 5
    }];
    setContent(prev => ({ ...prev, testimonials: newTestimonials }));
  };

  const removeTestimonial = (index) => {
    const newTestimonials = (content.testimonials || []).filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, testimonials: newTestimonials }));
  };

  if (loading) {
    return <div className="animate-pulse text-muted-foreground text-center py-8">Loading homepage content...</div>;
  }

  return (
    <div className="space-y-6" data-testid="homepage-editor">
      <div className="flex justify-between items-center">
        <h2 className="font-heading text-2xl">Homepage Content</h2>
        <Button onClick={handleSave} disabled={saving} data-testid="save-homepage-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Hero Section */}
      <div className="bg-card rounded-2xl p-6 shadow-sm space-y-4">
        <h3 className="font-medium text-lg">Hero Section</h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Label htmlFor="hero_headline">Headline</Label>
            <Input
              id="hero_headline"
              value={content?.hero?.headline || ''}
              onChange={(e) => updateHero('headline', e.target.value)}
              data-testid="input-hero-headline"
            />
          </div>
          <div>
            <Label htmlFor="hero_subheadline">Subheadline</Label>
            <Textarea
              id="hero_subheadline"
              value={content?.hero?.subheadline || ''}
              onChange={(e) => updateHero('subheadline', e.target.value)}
              rows={3}
              data-testid="input-hero-subheadline"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="hero_cta_text">CTA Button Text</Label>
              <Input
                id="hero_cta_text"
                value={content?.hero?.cta_text || ''}
                onChange={(e) => updateHero('cta_text', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="hero_cta_link">CTA Button Link</Label>
              <Input
                id="hero_cta_link"
                value={content?.hero?.cta_link || ''}
                onChange={(e) => updateHero('cta_link', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-card rounded-2xl p-6 shadow-sm space-y-4">
        <h3 className="font-medium text-lg">Statistics</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="stats_clients">Clients Helped</Label>
            <Input
              id="stats_clients"
              type="number"
              value={content?.stats_clients || 0}
              onChange={(e) => setContent(prev => ({ ...prev, stats_clients: parseInt(e.target.value) || 0 }))}
            />
          </div>
          <div>
            <Label htmlFor="stats_years">Years Experience</Label>
            <Input
              id="stats_years"
              type="number"
              value={content?.stats_years || 0}
              onChange={(e) => setContent(prev => ({ ...prev, stats_years: parseInt(e.target.value) || 0 }))}
            />
          </div>
          <div>
            <Label htmlFor="stats_sessions">Sessions Completed</Label>
            <Input
              id="stats_sessions"
              type="number"
              value={content?.stats_sessions || 0}
              onChange={(e) => setContent(prev => ({ ...prev, stats_sessions: parseInt(e.target.value) || 0 }))}
            />
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-card rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-medium text-lg">Features</h3>
          <Button variant="outline" size="sm" onClick={addFeature}>
            <Plus className="h-4 w-4 mr-1" /> Add Feature
          </Button>
        </div>
        <div className="space-y-4">
          {(content?.features || []).map((feature, index) => (
            <div key={feature.id || index} className="border border-border rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-start">
                <div className="grid grid-cols-2 gap-4 flex-1">
                  <div>
                    <Label>Icon Name</Label>
                    <Select
                      value={feature.icon}
                      onValueChange={(value) => updateFeature(index, 'icon', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Shield">Shield</SelectItem>
                        <SelectItem value="Heart">Heart</SelectItem>
                        <SelectItem value="Clock">Clock</SelectItem>
                        <SelectItem value="Award">Award</SelectItem>
                        <SelectItem value="Star">Star</SelectItem>
                        <SelectItem value="Users">Users</SelectItem>
                        <SelectItem value="Brain">Brain</SelectItem>
                        <SelectItem value="Leaf">Leaf</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={feature.title}
                      onChange={(e) => updateFeature(index, 'title', e.target.value)}
                    />
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeFeature(index)} className="text-destructive ml-2">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={feature.description}
                  onChange={(e) => updateFeature(index, 'description', e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="bg-card rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-medium text-lg">Testimonials</h3>
          <Button variant="outline" size="sm" onClick={addTestimonial}>
            <Plus className="h-4 w-4 mr-1" /> Add Testimonial
          </Button>
        </div>
        <div className="space-y-4">
          {(content?.testimonials || []).map((testimonial, index) => (
            <div key={testimonial.id || index} className="border border-border rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <Label>Quote</Label>
                  <Textarea
                    value={testimonial.quote}
                    onChange={(e) => updateTestimonial(index, 'quote', e.target.value)}
                    rows={3}
                  />
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeTestimonial(index)} className="text-destructive ml-2">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Author Name</Label>
                  <Input
                    value={testimonial.author_name}
                    onChange={(e) => updateTestimonial(index, 'author_name', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Author Title</Label>
                  <Input
                    value={testimonial.author_title || ''}
                    onChange={(e) => updateTestimonial(index, 'author_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Rating (1-5)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    value={testimonial.rating}
                    onChange={(e) => updateTestimonial(index, 'rating', parseInt(e.target.value) || 5)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ================== ABOUT PAGE EDITOR COMPONENT ==================
export const AboutEditor = () => {
  const { token } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/admin/cms/about`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load about page content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/cms/about`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('About page content saved successfully');
    } catch (error) {
      toast.error('Failed to save about page content');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const updateListItem = (field, index, value) => {
    const newList = [...(content[field] || [])];
    newList[index] = value;
    setContent(prev => ({ ...prev, [field]: newList }));
  };

  const addListItem = (field) => {
    const newList = [...(content[field] || []), ''];
    setContent(prev => ({ ...prev, [field]: newList }));
  };

  const removeListItem = (field, index) => {
    const newList = (content[field] || []).filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, [field]: newList }));
  };

  if (loading) {
    return <div className="animate-pulse text-muted-foreground text-center py-8">Loading about page content...</div>;
  }

  return (
    <div className="space-y-6" data-testid="about-editor">
      <div className="flex justify-between items-center">
        <h2 className="font-heading text-2xl">About Page Content</h2>
        <Button onClick={handleSave} disabled={saving} data-testid="save-about-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Therapist Info */}
      <div className="bg-card rounded-2xl p-6 shadow-sm space-y-4">
        <h3 className="font-medium text-lg">Therapist Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="therapist_name">Full Name</Label>
            <Input
              id="therapist_name"
              value={content?.therapist_name || ''}
              onChange={(e) => updateField('therapist_name', e.target.value)}
              data-testid="input-therapist-name"
            />
          </div>
          <div>
            <Label htmlFor="therapist_title">Professional Title</Label>
            <Input
              id="therapist_title"
              value={content?.therapist_title || ''}
              onChange={(e) => updateField('therapist_title', e.target.value)}
              data-testid="input-therapist-title"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="therapist_photo">Photo URL</Label>
            <Input
              id="therapist_photo"
              value={content?.therapist_photo || ''}
              onChange={(e) => updateField('therapist_photo', e.target.value)}
              placeholder="https://..."
            />
          </div>
        </div>
      </div>

      {/* Bio */}
      <div className="bg-card rounded-2xl p-6 shadow-sm space-y-4">
        <h3 className="font-medium text-lg">Biography</h3>
        <div>
          <Label htmlFor="bio_short">Short Bio (for cards/previews)</Label>
          <Textarea
            id="bio_short"
            value={content?.bio_short || ''}
            onChange={(e) => updateField('bio_short', e.target.value)}
            rows={3}
          />
        </div>
        <div>
          <Label htmlFor="bio_full">Full Bio</Label>
          <Textarea
            id="bio_full"
            value={content?.bio_full || ''}
            onChange={(e) => updateField('bio_full', e.target.value)}
            rows={8}
            placeholder="Use line breaks to separate paragraphs..."
          />
        </div>
      </div>

      {/* Qualifications */}
      <div className="bg-card rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-medium text-lg">Qualifications & Credentials</h3>
          <Button variant="outline" size="sm" onClick={() => addListItem('qualifications')}>
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {(content?.qualifications || []).map((qual, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={qual}
                onChange={(e) => updateListItem('qualifications', index, e.target.value)}
                placeholder="e.g., MSc in Psychology"
              />
              <Button variant="ghost" size="sm" onClick={() => removeListItem('qualifications', index)} className="text-destructive">
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Specializations */}
      <div className="bg-card rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-medium text-lg">Specializations</h3>
          <Button variant="outline" size="sm" onClick={() => addListItem('specializations')}>
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {(content?.specializations || []).map((spec, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={spec}
                onChange={(e) => updateListItem('specializations', index, e.target.value)}
                placeholder="e.g., Anxiety & Depression"
              />
              <Button variant="ghost" size="sm" onClick={() => removeListItem('specializations', index)} className="text-destructive">
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Approach */}
      <div className="bg-card rounded-2xl p-6 shadow-sm space-y-4">
        <h3 className="font-medium text-lg">Therapeutic Approach</h3>
        <div>
          <Label htmlFor="approach_title">Section Title</Label>
          <Input
            id="approach_title"
            value={content?.approach_title || ''}
            onChange={(e) => updateField('approach_title', e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="approach_description">Description</Label>
          <Textarea
            id="approach_description"
            value={content?.approach_description || ''}
            onChange={(e) => updateField('approach_description', e.target.value)}
            rows={4}
          />
        </div>
      </div>
    </div>
  );
};

// ================== SERVICES EDITOR COMPONENT ==================
export const ServicesEditor = () => {
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, service: null });
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/admin/cms/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (service = null) => {
    if (service) {
      setFormData({ ...service });
    } else {
      setFormData({
        name: '',
        slug: '',
        description: '',
        long_description: '',
        duration_minutes: 50,
        price: 100,
        category: 'individual',
        image_url: '',
        is_active: true
      });
    }
    setEditDialog({ open: true, service });
  };

  const handleSave = async () => {
    try {
      if (editDialog.service) {
        // Update existing
        await axios.put(`${API}/admin/cms/services/${editDialog.service.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Service updated successfully');
      } else {
        // Create new
        await axios.post(`${API}/admin/cms/services`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Service created successfully');
      }
      setEditDialog({ open: false, service: null });
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      await axios.delete(`${API}/admin/cms/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Service deleted successfully');
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete service');
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  if (loading) {
    return <div className="animate-pulse text-muted-foreground text-center py-8">Loading services...</div>;
  }

  return (
    <div className="space-y-6" data-testid="services-editor">
      <div className="flex justify-between items-center">
        <h2 className="font-heading text-2xl">Services</h2>
        <Button onClick={() => openEditDialog()} data-testid="add-service-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Service
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {services.map((service) => (
          <div key={service.id} className="bg-card rounded-2xl p-6 shadow-sm" data-testid={`service-card-${service.id}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-heading text-xl">{service.name}</h3>
                <p className="text-sm text-muted-foreground">/{service.slug}</p>
              </div>
              <div className="flex gap-2">
                <Badge className={service.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                  {service.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{service.description}</p>
            <div className="flex justify-between items-center">
              <div className="text-sm">
                <span className="font-medium">£{service.price}</span>
                <span className="text-muted-foreground"> / {service.duration_minutes} min</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEditDialog(service)}>
                  Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(service.id)} className="text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editDialog.service ? 'Edit Service' : 'Create New Service'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="service_name">Service Name</Label>
                <Input
                  id="service_name"
                  value={formData.name || ''}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      name: e.target.value,
                      slug: editDialog.service ? prev.slug : generateSlug(e.target.value)
                    }));
                  }}
                />
              </div>
              <div>
                <Label htmlFor="service_slug">URL Slug</Label>
                <Input
                  id="service_slug"
                  value={formData.slug || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  disabled={!!editDialog.service}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="service_description">Short Description</Label>
              <Textarea
                id="service_description"
                value={formData.description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="service_long_description">Full Description (for service page)</Label>
              <Textarea
                id="service_long_description"
                value={formData.long_description || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, long_description: e.target.value }))}
                rows={6}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="service_price">Price (£)</Label>
                <Input
                  id="service_price"
                  type="number"
                  value={formData.price || 0}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label htmlFor="service_duration">Duration (minutes)</Label>
                <Input
                  id="service_duration"
                  type="number"
                  value={formData.duration_minutes || 50}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 50 }))}
                />
              </div>
              <div>
                <Label htmlFor="service_category">Category</Label>
                <Select
                  value={formData.category || 'individual'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="couples">Couples</SelectItem>
                    <SelectItem value="consultation">Consultation</SelectItem>
                    <SelectItem value="group">Group</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="service_image">Image URL</Label>
              <Input
                id="service_image"
                value={formData.image_url || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                placeholder="https://..."
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="service_active"
                checked={formData.is_active !== false}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="service_active">Service is active and bookable</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, service: null })}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-service-btn">
              {editDialog.service ? 'Update Service' : 'Create Service'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default { SiteSettingsEditor, HomepageEditor, AboutEditor, ServicesEditor };
